/**
 * SectionConfigurator Component
 * Amit's admin tool for configuring prospect portals
 * Drag/drop sections, toggle visibility, set tier, preview
 */

import React, { useState, useCallback, useEffect } from 'react';
import { configAPI } from '../../utils/api';
import {
  getDefaultConfigForTier,
  getAllSectionMetadata,
  reorderSections,
  toggleSectionVisibility,
  getVisibleSections,
  addSection,
  removeSection,
  mergeConfigWithDefaults,
} from '../../utils/sections';

const styles = {
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    padding: '40px',
    backgroundColor: '#0F1729',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#FFFFFF',
  },
  sidebarContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  section: {
    backgroundColor: '#1A2332',
    borderRadius: '8px',
    border: '1px solid #2A3444',
    padding: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tierSelector: {
    display: 'flex',
    gap: '8px',
  },
  tierButton: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '6px',
    border: '1px solid #2A3444',
    backgroundColor: 'transparent',
    color: '#BBBEC7',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  tierButtonActive: {
    borderColor: '#0057FF',
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
  },
  sectionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '600px',
    overflowY: 'auto',
  },
  sectionItem: {
    padding: '12px',
    borderRadius: '6px',
    backgroundColor: '#0F1729',
    border: '1px solid #2A3444',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'move',
    transition: 'all 0.3s ease',
  },
  sectionItemDragging: {
    opacity: 0.5,
    backgroundColor: '#2A3444',
  },
  sectionItemHidden: {
    opacity: 0.5,
  },
  sectionItemName: {
    fontSize: '14px',
    fontWeight: '500',
  },
  toggleButton: {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #2A3444',
    backgroundColor: 'transparent',
    color: '#BBBEC7',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  toggleButtonActive: {
    borderColor: '#00D4AA',
    color: '#00D4AA',
  },
  availableSections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  addSectionButton: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #0057FF',
    backgroundColor: 'transparent',
    color: '#0057FF',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  addSectionButtonHover: {
    backgroundColor: 'rgba(0, 87, 255, 0.1)',
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100vh',
    overflow: 'hidden',
  },
  previewFrame: {
    flex: 1,
    borderRadius: '8px',
    border: '1px solid #2A3444',
    backgroundColor: '#0F1729',
    overflow: 'hidden',
  },
  previewIframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
  },
  button: {
    flex: 1,
    padding: '12px 20px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  primaryButton: {
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
  },
  primaryButtonHover: {
    backgroundColor: '#003D99',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    border: '1px solid #2A3444',
    color: '#BBBEC7',
  },
  secondaryButtonHover: {
    borderColor: '#00D4AA',
    color: '#00D4AA',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '4px solid #2A3444',
    borderTop: '4px solid #0057FF',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

/**
 * DraggableSectionItem component
 */
function DraggableSectionItem({
  section,
  metadata,
  onDragStart,
  onDragEnd,
  onToggleVisibility,
  onRemove,
}) {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <div
      style={{
        ...styles.sectionItem,
        ...(isDragging && styles.sectionItemDragging),
        ...(!section.visible && styles.sectionItemHidden),
      }}
      draggable={!metadata?.alwaysOn}
      onDragStart={(e) => {
        setIsDragging(true);
        onDragStart(e, section);
      }}
      onDragEnd={(e) => {
        setIsDragging(false);
        onDragEnd(e);
      }}
    >
      <span style={styles.sectionItemName}>{metadata?.name}</span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          style={{
            ...styles.toggleButton,
            ...(section.visible && styles.toggleButtonActive),
          }}
          onClick={() => onToggleVisibility(section.id, !section.visible)}
        >
          {section.visible ? 'Hide' : 'Show'}
        </button>
        {!metadata?.alwaysOn && (
          <button
            style={{ ...styles.toggleButton, color: '#FF6B6B', borderColor: '#FF6B6B' }}
            onClick={() => onRemove(section.id)}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * SectionConfigurator Component
 */
function SectionConfigurator({ prospectId }) {
  const [tier, setTier] = useState('smb');
  const [config, setConfig] = useState(getDefaultConfigForTier('smb'));
  const [draggedSection, setDraggedSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [allMetadata] = useState(getAllSectionMetadata());

  // Load current config if editing existing prospect
  useEffect(() => {
    if (!prospectId) return;

    const loadConfig = async () => {
      try {
        const response = await configAPI.getSectionConfigs();
        const existingConfig = response.configs?.[prospectId];
        if (existingConfig) {
          setConfig(existingConfig);
          setTier(existingConfig.tier);
        }
      } catch (err) {
        console.warn('Failed to load config:', err);
      }
    };

    loadConfig();
  }, [prospectId]);

  // Add CSS animations
  useEffect(() => {
    if (!document.querySelector('style[data-configurator-animations]')) {
      const style = document.createElement('style');
      style.setAttribute('data-configurator-animations', 'true');
      style.textContent = `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleTierChange = useCallback((newTier) => {
    setTier(newTier);
    const defaultConfig = getDefaultConfigForTier(newTier);
    setConfig(defaultConfig);
  }, []);

  const handleDragStart = useCallback((e, section) => {
    setDraggedSection(section);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedSection(null);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, targetSection) => {
    e.preventDefault();
    if (!draggedSection || draggedSection.id === targetSection.id) return;

    const visibleSections = getVisibleSections(config);
    const draggedIndex = visibleSections.findIndex((s) => s.id === draggedSection.id);
    const targetIndex = visibleSections.findIndex((s) => s.id === targetSection.id);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder sections
    const newOrder = visibleSections
      .filter((s) => s.id !== draggedSection.id)
      .map((s) => s.id);
    newOrder.splice(targetIndex, 0, draggedSection.id);

    const reordered = reorderSections(config, newOrder);
    setConfig(reordered);
  }, [config, draggedSection]);

  const handleToggleVisibility = useCallback((sectionId, visible) => {
    const updated = toggleSectionVisibility(config, sectionId, visible);
    setConfig(updated);
  }, [config]);

  const handleRemoveSection = useCallback((sectionId) => {
    const updated = removeSection(config, sectionId);
    setConfig(updated);
  }, [config]);

  const handleAddSection = useCallback((sectionId) => {
    const metadata = allMetadata.find((m) => m.id === sectionId);
    if (!metadata) return;

    const currentSectionIds = config.sections.map((s) => s.id);
    if (currentSectionIds.includes(sectionId)) return; // Already added

    const updated = addSection(config, sectionId);
    setConfig(updated);
  }, [config, allMetadata]);

  const handleSave = useCallback(async () => {
    if (!prospectId) {
      setSaveStatus('error');
      return;
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      await configAPI.updateSectionConfig(prospectId, config);
      await configAPI.updateProspectTier(prospectId, tier);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Failed to save config:', err);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  }, [prospectId, config, tier]);

  const handleReset = useCallback(() => {
    const defaultConfig = getDefaultConfigForTier(tier);
    setConfig(defaultConfig);
  }, [tier]);

  // Get sections not yet added
  const addedSectionIds = config.sections.map((s) => s.id);
  const availableSections = allMetadata.filter(
    (m) => !addedSectionIds.includes(m.id) && !m.alwaysOn
  );

  const visibleSections = getVisibleSections(config);

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebarContainer}>
        {/* Tier Selector */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Prospect Tier</div>
          <div style={styles.tierSelector}>
            {['smb', 'midmarket', 'enterprise'].map((t) => (
              <button
                key={t}
                style={{
                  ...styles.tierButton,
                  ...(tier === t && styles.tierButtonActive),
                }}
                onClick={() => handleTierChange(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Current Sections */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Sections</div>
          <div
            style={styles.sectionsList}
            onDragOver={handleDragOver}
          >
            {config.sections
              .sort((a, b) => a.order - b.order)
              .map((section) => {
                const metadata = allMetadata.find((m) => m.id === section.id);
                return (
                  <DraggableSectionItem
                    key={section.id}
                    section={section}
                    metadata={metadata}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onToggleVisibility={handleToggleVisibility}
                    onRemove={handleRemoveSection}
                  />
                );
              })}
          </div>
        </div>

        {/* Add Sections */}
        {availableSections.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Add Sections</div>
            <div style={styles.availableSections}>
              {availableSections.map((metadata) => (
                <button
                  key={metadata.id}
                  style={styles.addSectionButton}
                  onClick={() => handleAddSection(metadata.id)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(0, 87, 255, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  + {metadata.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={styles.section}>
          <div style={styles.actionButtons}>
            <button
              style={styles.button + Object.entries(styles.primaryButton).reduce((s, [k, v]) => s, '')}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              style={styles.button + Object.entries(styles.secondaryButton).reduce((s, [k, v]) => s, '')}
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset
            </button>
          </div>
          {saveStatus === 'success' && (
            <div style={{ color: '#00D4AA', fontSize: '12px', marginTop: '12px', textAlign: 'center' }}>
              Saved successfully!
            </div>
          )}
          {saveStatus === 'error' && (
            <div style={{ color: '#FF6B6B', fontSize: '12px', marginTop: '12px', textAlign: 'center' }}>
              Error saving configuration
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      <div style={styles.previewContainer}>
        <h2>Portal Preview</h2>
        <div style={{ position: 'relative', ...styles.previewFrame }}>
          {isSaving && (
            <div style={styles.loadingOverlay}>
              <div style={styles.spinner} />
            </div>
          )}
          <div
            style={{
              padding: '20px',
              overflowY: 'auto',
              height: '100%',
              backgroundColor: '#0F1729',
            }}
          >
            <div style={{ fontSize: '12px', color: '#BBBEC7', marginBottom: '20px' }}>
              Visible sections ({visibleSections.length}):
            </div>
            {visibleSections.map((section) => {
              const metadata = allMetadata.find((m) => m.id === section.id);
              return (
                <div
                  key={section.id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    borderRadius: '6px',
                    backgroundColor: '#1A2332',
                    border: '1px solid #2A3444',
                    fontSize: '13px',
                  }}
                >
                  <strong>{metadata?.name}</strong>
                  <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                    {metadata?.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

SectionConfigurator.propTypes = {
  prospectId: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'string') {
      return new Error(`${propName} must be a string`);
    }
  },
};

export default SectionConfigurator;
